import { useLocation } from "react-router-dom";
import FileNotFound from "files/components/FileNotFound";
import FilePreview from "files/components/FilePreview";
import { useFilesInfinite } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { Directory } from "~/components/ui/directory";

interface PrefixFileListingProps {
  /** Where the parent-navigation row points; the folder list, at the top of an attached folder. */
  backTo: string;
}

/**
 * Contents of one folder attached to a dataset, paginated a page at a time.
 *
 * Shares the file browser's directory listing rather than restating it, so that
 * pagination, row actions, and previews stay identical across the two surfaces.
 */
const PrefixFileListing = ({ backTo }: PrefixFileListingProps) => {
  const { state } = useLocation();
  const preview = state?.preview as Pick<FileInfo, "key" | "size"> | undefined;
  const { data, allChildren, isLoading, isLoadingMore, hasMore, loadMore, error } =
    useFilesInfinite(/* retryWhenNotReady= */ true);

  if (!isLoading && !error && !data?.current_file) {
    return <FileNotFound />;
  }

  return (
    <div data-testid="dataset-file-directory">
      <Directory
        navigation="url-based"
        files={allChildren}
        loading={isLoading}
        backTo={backTo}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
        error={error ? "Unable to load files" : undefined}
      />
      {preview && <FilePreview file={preview} />}
    </div>
  );
};

export default PrefixFileListing;
